import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

const uuidSchema = z.string().uuid();
const positiveIntegerSchema = z.number().int().positive();
const contentTypeSchema = z.enum(['image/jpeg', 'image/png', 'application/pdf']);

export const receiptOutSchema = z.object({
  receipt_id: uuidSchema,
  line_item_id: uuidSchema,
  content_type: contentTypeSchema,
  byte_size: positiveIntegerSchema.max(10_485_760),
  version: positiveIntegerSchema,
  can_inline_view: z.boolean(),
  created_at: z.string().datetime({ offset: true }),
}).strict().superRefine((receipt, context) => {
  const expectedInline = receipt.content_type !== 'application/pdf';
  if (receipt.can_inline_view !== expectedInline) {
    context.addIssue({ code: 'custom', path: ['can_inline_view'], message: 'inline behavior must match content type' });
  }
});

export type ReceiptOut = z.infer<typeof receiptOutSchema>;

export const receiptMutationMetaSchema = z.object({
  operation_id: uuidSchema,
  batch_id: uuidSchema,
  audit_ids: z.array(positiveIntegerSchema).min(1),
  idempotent_replay: z.boolean(),
}).strict().superRefine((meta, context) => {
  if (meta.batch_id !== meta.operation_id) {
    context.addIssue({ code: 'custom', path: ['batch_id'], message: 'batch_id must equal operation_id' });
  }
});

export type ReceiptMutationMeta = z.infer<typeof receiptMutationMetaSchema>;

export const receiptDeleteOutSchema = z.object({
  storage_cleanup_status: z.literal('pending'),
}).strict();

export type ReceiptDeleteOut = z.infer<typeof receiptDeleteOutSchema>;

const operationIdSchema = uuidSchema;
const positiveVersionTextSchema = z.string().regex(/^[1-9][0-9]*$/, 'expected a canonical positive decimal');

export class ReceiptRequestError extends Error {
  readonly code: string;
  readonly status: number | undefined;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = 'ReceiptRequestError';
    this.code = code;
    this.status = status;
  }
}

function safeMessage(code: string, status: number | undefined): string {
  if (code === 'receipt_storage_unavailable') return 'Receipt storage is unavailable.';
  if (status === 409) return 'This receipt request could not be completed.';
  if (status !== undefined && status >= 500) return 'Receipt service is unavailable.';
  return 'Receipt request failed.';
}

function toReceiptError(error: unknown): ReceiptRequestError {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  const payload = isAxiosError(error) ? error.response?.data : undefined;
  if (isErrorEnvelope(payload)) {
    return new ReceiptRequestError(payload.error.code, safeMessage(payload.error.code, status), status);
  }
  return new ReceiptRequestError(status === 409 ? 'conflict' : 'request_failed', safeMessage('request_failed', status), status);
}

function operationId(): string {
  const generated = globalThis.crypto?.randomUUID?.();
  if (!generated) throw new ReceiptRequestError('operation_id_unavailable', 'Receipt request could not be started.');
  return operationIdSchema.parse(generated);
}

export function createReceiptOperationId(): string {
  return operationId();
}

function expectedVersionText(version: number): string {
  return positiveVersionTextSchema.parse(String(positiveIntegerSchema.parse(version)));
}

type ReceiptHookOptions = {
  lineItemId: string;
  lineItemVersion: number;
  canonicalWriteContext: string;
  transactionId: string;
};

export type ReceiptUploadInput = {
  file: File;
  operationId?: string;
  onProgress?: (percent: number) => void;
};

export type ReceiptDeleteInput = {
  receiptId: string;
  expectedVersion: number;
  operationId?: string;
};

export type ReceiptBlobHandle =
  | { kind: 'image'; url: string; revoke: () => void }
  | { kind: 'download'; revoke: () => void };

function detailQueryKey(transactionId: string) {
  return ['transactions', 'detail', transactionId] as const;
}

function makeRevocableUrl(blob: Blob) {
  const url = URL.createObjectURL(blob);
  let revoked = false;
  return {
    url,
    revoke: () => {
      if (revoked) return;
      revoked = true;
      URL.revokeObjectURL(url);
    },
  };
}

export function useReceipts({ lineItemId, lineItemVersion, canonicalWriteContext, transactionId }: ReceiptHookOptions) {
  const queryClient = useQueryClient();

  const invalidateDetail = async () => {
    await queryClient.invalidateQueries({ queryKey: detailQueryKey(transactionId) });
  };

  const upload = useMutation<ReceiptOut, ReceiptRequestError, ReceiptUploadInput>({
    mutationFn: async ({ file, operationId: requestedOperationId, onProgress }) => {
      const form = new FormData();
      form.append('operation_id', operationIdSchema.parse(requestedOperationId ?? operationId()));
      form.append('expected_line_item_version', expectedVersionText(lineItemVersion));
      form.append('canonical_write_context', canonicalWriteContext);
      form.append('file', file);
      try {
        const response = await client.post(`/api/line-items/${lineItemId}/receipts`, form, {
          onUploadProgress: (event) => {
            if (event.total && event.total > 0) {
              onProgress?.(Math.min(100, Math.round((event.loaded / event.total) * 100)));
            }
          },
        });
        const parsed = parseEnvelope(receiptOutSchema, response.data);
        receiptMutationMetaSchema.parse(parsed.meta);
        return parsed.data;
      } catch (error) {
        if (error instanceof z.ZodError) throw error;
        throw toReceiptError(error);
      }
    },
    retry: false,
    onSuccess: invalidateDetail,
  });

  const deleteReceipt = useMutation<ReceiptDeleteOut, ReceiptRequestError, ReceiptDeleteInput>({
    mutationFn: async ({ receiptId, operationId: requestedOperationId, expectedVersion }) => {
      const body = {
        operation_id: operationIdSchema.parse(requestedOperationId ?? operationId()),
        expected_version: positiveIntegerSchema.parse(expectedVersion),
      };
      try {
        const response = await client.delete(`/api/line-items/${lineItemId}/receipts/${uuidSchema.parse(receiptId)}`, { data: body });
        const parsed = parseEnvelope(receiptDeleteOutSchema, response.data);
        receiptMutationMetaSchema.parse(parsed.meta);
        return parsed.data;
      } catch (error) {
        if (error instanceof z.ZodError) throw error;
        throw toReceiptError(error);
      }
    },
    retry: false,
    onSuccess: invalidateDetail,
  });

  async function viewOrDownload(receipt: ReceiptOut): Promise<ReceiptBlobHandle> {
    let blob: Blob;
    try {
      const response = await client.get(`/api/line-items/${lineItemId}/receipts/${uuidSchema.parse(receipt.receipt_id)}`, { responseType: 'blob' });
      blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: receipt.content_type });
    } catch (error) {
      throw toReceiptError(error);
    }

    const handle = makeRevocableUrl(blob);
    if (receipt.content_type === 'application/pdf' || !receipt.can_inline_view) {
      const anchor = document.createElement('a');
      anchor.href = handle.url;
      anchor.download = 'receipt.pdf';
      anchor.rel = 'noopener';
      anchor.click();
      window.setTimeout(handle.revoke, 0);
      return { kind: 'download', revoke: handle.revoke };
    }
    return { kind: 'image', url: handle.url, revoke: handle.revoke };
  }

  return { upload, deleteReceipt, viewOrDownload };
}
